import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import Header from './components/Header/Header';
import Footer from './components/Footer/Footer';
import Login from './pages/Login/Login';
import OrderHistory from './pages/OrderHistory/OrderHistory';
import NewOrder from './pages/NewOrder/NewOrder';
import SentryErrorBoundary from './components/SentryErrorBoundary';
import { defaultErrorHandler } from './services/ErrorHandler';
import { captureException } from './services/sentry';
import { ErrorType } from './models/ApiError';

// Configure React Query with Sentry error logging
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      onError: (error) => {
        // Convert to ApiError and send to Sentry
        const apiError = defaultErrorHandler.handle(error);
        if (apiError.type !== ErrorType.VALIDATION_ERROR) {
          captureException(error instanceof Error ? error : new Error(String(error)), {
            tags: {
              source: 'react-query',
              mutation: 'true',
            },
          });
        }
      },
    },
  },
});

const App: React.FC = () => {
  const { i18n } = useTranslation();
  const isHebrew = i18n.language === 'he';

  return (
    <SentryErrorBoundary>
      <AuthProvider>
        <QueryClientProvider client={queryClient}>
          <Router>
            <div id="root">
              <Header />
              <main dir={isHebrew ? 'rtl' : 'ltr'}>
                <Routes>
                  <Route path="/" element={<Login />} />
                  <Route path="/order-history" element={<OrderHistory />} />
                  <Route path="/new-order" element={<NewOrder />} />
                </Routes>
              </main>
              <div dir={isHebrew ? 'rtl' : 'ltr'}>
                <Footer />
              </div>
            </div>
          </Router>
        </QueryClientProvider>
      </AuthProvider>
    </SentryErrorBoundary>
  );
};

export default App;